import { useEffect, useMemo, useState } from "react";
import { tryApiGet } from "../api/client";
import { TRUST_TIER_INFO, normalizeTrustTier } from '../lib/definitions/trust';
import { RadioControlOperationCard } from "./RadioControlOperationCard";
import { SectionCard } from "./SectionCard";
import type { HeardByEntry, RadioControlCapabilitiesResponse, RadioControlOperation, RadioControlTransportCapabilities } from "../api/types";

// Node-detail Commands section (2026-09-23 reorder/Commands pass, Change
// 3). The rest of the radio-control surface (RadioControlPanel.tsx /
// RadioControlOperationCard.tsx, read in full before writing this) is
// about configuring OUR OWN radio. This is the opposite direction: acting
// on the node being viewed. It reuses RadioControlOperationCard entirely
// for the actual execute flow (param form, dry-run-free Apply, destructive
// type-to-confirm, <redacted> stripping) — nothing about that is
// reimplemented here. What's new is: (1) picking which of the registry's
// operations even have this node as their subject, and (2) picking which
// of our transports can plausibly reach it.
//
// (1) is detected purely by PARAM SHAPE — an operation qualifies if it has
// a param literally named "node_id", "destination", or "public_key" (the
// three real names RADIO_CONTROL_CONTRACT.md section 2 uses across both
// protocols for "the other node this acts on"). Never by operation id —
// same rule RadioControlPanel.tsx's own header comment states, and
// RADIO_CONTROL_CONTRACT.md section 2 makes explicit ("nothing anywhere
// else may hardcode an operation id"). That qualifying param is then
// pre-bound via RadioControlOperationCard's existing `fixedParams` prop to
// this page's own node id — the viewed node IS the subject, so it's never
// re-typed.
const NODE_SUBJECT_PARAM_NAMES = new Set(["node_id", "destination", "public_key"]);

interface NodeSubjectOp {
  op: RadioControlOperation;
  paramName: string;
}

function nodeSubjectParamName(op: RadioControlOperation): string | null {
  const params = Array.isArray(op.params) ? op.params : [];
  const found = params.find((p) => NODE_SUBJECT_PARAM_NAMES.has(p.name));
  return found ? found.name : null;
}

function nodeSubjectOps(transport: RadioControlTransportCapabilities): NodeSubjectOp[] {
  const ops = Array.isArray(transport.operations) ? transport.operations : [];
  const out: NodeSubjectOp[] = [];
  for (const op of ops) {
    const paramName = nodeSubjectParamName(op);
    if (paramName) out.push({ op, paramName });
  }
  return out;
}

// Groups already-node-subject-filtered ops by the backend's own `category`
// field (same field RadioControlPanel.tsx groups on) — first-appearance
// order, uncategorised ("") last. No fixed category ordering table here:
// unlike the full radio-control surface, this list is short enough
// (typically 3-6 ops on one transport) that first-appearance order reads
// fine without one.
function groupByCategory(items: NodeSubjectOp[]): { category: string; items: NodeSubjectOp[] }[] {
  const order: string[] = [];
  const buckets = new Map<string, NodeSubjectOp[]>();
  for (const item of items) {
    const cat = item.op.category && item.op.category.trim() ? item.op.category : "";
    let bucket = buckets.get(cat);
    if (!bucket) {
      bucket = [];
      buckets.set(cat, bucket);
      order.push(cat);
    }
    bucket.push(item);
  }
  order.sort((a, b) => (a === "" ? 1 : 0) - (b === "" ? 1 : 0));
  return order.map((category) => ({ category, items: buckets.get(category)! }));
}

export function NodeCommandsSection({
  nodeId,
  protocol,
  heardBy,
}: {
  // Same native id this page already sends on every other per-node
  // endpoint (history, reconciliation) — Meshtastic "!hex" or MeshCore's
  // 64-hex public key, already in the right form, never reformatted here.
  nodeId: string;
  protocol: "meshtastic" | "meshcore" | "unknown";
  heardBy: HeardByEntry[];
}) {
  const [capabilities, setCapabilities] = useState<RadioControlCapabilitiesResponse | null | "unavailable">(null);

  useEffect(() => {
    let cancelled = false;
    tryApiGet<RadioControlCapabilitiesResponse>("/api/radio/control/capabilities")
      .then((res) => !cancelled && setCapabilities(res ?? "unavailable"))
      .catch(() => !cancelled && setCapabilities("unavailable"));
    return () => {
      cancelled = true;
    };
  }, []);

  const transports = useMemo(
    () => (capabilities && capabilities !== "unavailable" && Array.isArray(capabilities.transports) ? capabilities.transports : []),
    [capabilities],
  );

  // Transports of the same protocol as this node — the universe this
  // section could ever act through, before asking whether any of them
  // actually heard this specific node.
  const transportsForProtocol = useMemo(() => transports.filter((t) => t.protocol === protocol), [transports, protocol]);

  // (2) Which of those transports plausibly reach THIS node: it must have
  // actually heard it, first-party — observer_id and transport_id share
  // the same namespace (both are the transport's config key, e.g.
  // "meshtastic_vnode" — confirmed against backend/config/config.yaml's
  // own `transports:` block and ingest's use of that key as observer_id).
  // "First-party" here is TRUST_TIER_INFO's own `ownRadio` flag — exactly
  // the set {first_party, first_party_local_network, first_party_unsigned}
  // the task calls out; reused from definitions.ts rather than
  // re-listing those three strings a second time. relayed_mqtt is
  // explicitly NOT in that set (ownRadio: false) — someone else's radio
  // relaying to us doesn't mean OUR transport can reach this node.
  const firstPartyObserverIds = useMemo(
    () => new Set(heardBy.filter((h) => TRUST_TIER_INFO[normalizeTrustTier(h.trust_tier)].ownRadio).map((h) => h.observer_id)),
    [heardBy],
  );

  const qualifyingTransports = useMemo(
    () => transportsForProtocol.filter((t) => firstPartyObserverIds.has(t.transport_id)),
    [transportsForProtocol, firstPartyObserverIds],
  );

  const [selectedTransportId, setSelectedTransportId] = useState<string | null>(null);
  useEffect(() => {
    if (qualifyingTransports.length === 0) {
      setSelectedTransportId(null);
      return;
    }
    if (!selectedTransportId || !qualifyingTransports.some((t) => t.transport_id === selectedTransportId)) {
      setSelectedTransportId(qualifyingTransports[0].transport_id);
    }
  }, [qualifyingTransports, selectedTransportId]);

  const selectedTransport = qualifyingTransports.find((t) => t.transport_id === selectedTransportId) ?? null;

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  function toggleCard(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Whether this protocol has any node-subject operation AT ALL, on any
  // transport of that protocol — decides whether this section exists on
  // this node's page at all, independent of whether any transport
  // currently qualifies to run one (that's the narrower, transport-gated
  // question handled below).
  const anyNodeSubjectOpsForProtocol = useMemo(
    () => transportsForProtocol.some((t) => nodeSubjectOps(t).length > 0),
    [transportsForProtocol],
  );

  // Nothing to bind a node-subject operation to without a known protocol,
  // and nothing to show if the registry has no such operation for this
  // protocol at all — omit the whole section rather than render an empty
  // card (same convention as ReconciliationSection).
  if (protocol === "unknown") return null;
  if (capabilities === "unavailable") return null;
  if (capabilities !== null && !anyNodeSubjectOpsForProtocol) return null;

  if (capabilities === null) {
    return (
      <SectionCard title="Commands">
        <div className="node-detail-note">Loading…</div>
      </SectionCard>
    );
  }

  if (qualifyingTransports.length === 0) {
    return (
      <SectionCard title="Commands">
        <div className="node-detail-note">
          We have no transport that heard this node directly — no first-party {protocol} observer of ours has ever reported
          it, so there's nothing safe to run a command through.
        </div>
      </SectionCard>
    );
  }

  const items = selectedTransport ? nodeSubjectOps(selectedTransport) : [];
  const groups = groupByCategory(items);

  return (
    <SectionCard title="Commands" description="Operations that act on this node, run over one of our own radios.">
      {qualifyingTransports.length > 1 && (
        <div className="settings-tabs">
          {qualifyingTransports.map((t) => (
            <button
              key={t.transport_id}
              type="button"
              className="settings-tab"
              data-active={selectedTransportId === t.transport_id ? "true" : "false"}
              onClick={() => setSelectedTransportId(t.transport_id)}
            >
              {t.label || t.transport_id}
            </button>
          ))}
        </div>
      )}

      {items.length === 0 ? (
        <div className="node-detail-note">
          {selectedTransport?.label || selectedTransport?.transport_id} reports no operations whose subject is another node.
        </div>
      ) : (
        groups.map((sub, subIdx) => (
          <div className="radio-control-category-group" key={sub.category || `uncategorised-${subIdx}`}>
            {sub.category && <h6 className="radio-control-category-title">{sub.category}</h6>}
            {/* No group-level notes block here (2026-09-24 fix): every
                operation in this section binds to exactly one card (the
                viewed node is pre-bound via fixedParams, but that never
                expands one op into several cards the way
                RadioControlPanel's Node Configuration section does) — so
                op.label/summary/rf_note belong on that one card, not
                printed again above it. RadioControlOperationCard shows its
                own copy by default (hideOwnCopy left unset here); see that
                component's own header comment for the one-card-vs-many
                rule this now follows. */}
            {sub.items.map(({ op, paramName }) => {
              const anchorId = `node-cmd-${selectedTransport!.transport_id}-${op.id}`;
              return (
                <RadioControlOperationCard
                  key={op.id}
                  transportId={selectedTransport!.transport_id}
                  op={op}
                  entries={null}
                  fixedParams={{ [paramName]: nodeId }}
                  anchorId={anchorId}
                  expanded={expandedIds.has(anchorId)}
                  onToggle={() => toggleCard(anchorId)}
                />
              );
            })}
          </div>
        ))
      )}
    </SectionCard>
  );
}
